'use client';

import React, { useEffect, useRef } from 'react';
import LandingNav from './components/LandingNav';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import PsychWorldSection from './components/PsychWorldSection';
import XPBadgeSection from './components/XPBadgeSection';
import IDCardShowcase from './components/IDCardShowcase';
import IPExpansionSection from './components/IPExpansionSection';
import CTASection from './components/CTASection';
import LandingFooter from './components/LandingFooter';

const STAR_COUNT = 180;

function generateStars() {
  return Array.from({ length: STAR_COUNT }, (_, i) => ({
    id: i,
    x: (i * 137.508 + 23) % 100,
    y: (i * 97.333 + 11) % 100,
    size: i % 3 === 0 ? 2 : i % 5 === 0 ? 1.5 : 1,
    opacity: 0.2 + (i % 7) * 0.1,
    duration: 2 + (i % 5),
    delay: (i % 10) * 0.4,
  }));
}

const stars = generateStars();

export default function LandingPage() {
  const spotlightRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const timeRef = useRef<number>(0);

  useEffect(() => {
    let lastTime = 0;

    const animate = (timestamp: number) => {
      const delta = timestamp - lastTime;
      lastTime = timestamp;
      timeRef.current += delta * 0.0004; // medium speed

      const t = timeRef.current;
      // Lissajous-style path for smooth, non-repetitive movement
      const x = 50 + 38 * Math.sin(t * 1.3);
      const y = 50 + 32 * Math.cos(t * 0.9);

      if (spotlightRef.current) {
        spotlightRef.current.style.left = `${x}%`;
        spotlightRef.current.style.top = `${y}%`;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Stars layer */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        {stars.map((star) => (
          <span
            key={star.id}
            className="absolute rounded-full bg-white animate-star-twinkle"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animationDuration: `${star.duration}s`,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Animated blue spotlight */}
      <div
        ref={spotlightRef}
        className="fixed pointer-events-none z-0"
        aria-hidden="true"
        style={{
          width: '700px',
          height: '700px',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(26,107,138,0.18) 0%, rgba(26,107,138,0.07) 40%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(8px)',
          transition: 'left 0.05s linear, top 0.05s linear',
        }}
      />

      {/* Page content */}
      <div className="relative z-10">
        <LandingNav />
        <HeroSection />
        <FeaturesSection />
        <PsychWorldSection />
        <XPBadgeSection />
        <IDCardShowcase />
        <IPExpansionSection />
        <CTASection />
        <LandingFooter />
      </div>
    </div>
  );
}